// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo
import type { AuditMetadataModelType } from './AuditMetadata';
import type { ActorModelType } from './Actor';
import type { LatLngModelType } from './LatLng';
import type { MasterOutletModelType } from './MasterOutlet';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';

export type OuterPolygonType = {
    points: Array<LatLngModelType>
};

export type TerritoryOutletIdModelType = {
    clientId: string,
    outletId: string,
    version?: number
};

export type TerritoryOutletInfoModelType = {
    outletIds: Array<TerritoryOutletIdModelType>,
    outlets?: Array<MasterOutletModelType>
};

export type TerritoryMetricsModelType = {
    count: number,
    totalRevenue: number,
    totalTAT: string
};

export type MasterTerritoryModelType = {
    clientId: string,
    id: string,
    name: string,
    code: string,
    status: StatusEnum,
    version: number,
    customProperties: { [key: string]: string },
    auditMetadata: ?AuditMetadataModelType,
    outerPolygon: OuterPolygonType,
    outletsInfo: TerritoryOutletInfoModelType,
    metrics: TerritoryMetricsModelType
};

export type MasterTerritoryPlanModelType = {
    clientId: string,
    id: string,
    name: string,
    code: string,
    status: StatusEnum,
    version: number,
    customProperties: { [key: string]: string },
    auditMetadata: ?AuditMetadataModelType,
    territories: Array<MasterTerritoryModelType>,
    actor: ActorModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TerritoryOutletInfo extends ImmutableModel {
    static fromJS(json: TerritoryOutletInfoModelType): TerritoryOutletInfo {
        const state: Object = Object.assign({}, json);
        state.outletIds = Immutable.List(state.outletIds).map(item =>
            TerritoryOutletId.fromJS(item)
        );
        state.outlets = state.outlets
            ? Immutable.List(state.outlets).map(item =>
                  MasterOutlet.fromJS(item)
              )
            : state.outlets;
        return new this(Immutable.Map(state));
    }

    toJS(): TerritoryOutletInfoModelType {
        const js: TerritoryOutletInfoModelType = {
            outletIds: this.outletIds.toArray().map(item => item.toJS())
        };

        if (this.outlets != null) {
            js.outlets = this.outlets
                ? this.outlets.toArray().map(item => item.toJS())
                : this.outlets;
        }

        return js;
    }

    get outletIds(): Immutable.List<TerritoryOutletId> {
        return this._state.get('outletIds');
    }

    setOutletIds(outletIds: Immutable.List<TerritoryOutletId>): this {
        return this.clone(this._state.set('outletIds', outletIds));
    }

    get outlets(): ?Immutable.List<MasterOutlet> {
        return this._state.get('outlets');
    }

    setOutlets(outlets: ?Immutable.List<MasterOutlet>): this {
        return this.clone(this._state.set('outlets', outlets));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterTerritory extends ImmutableModel {
    static fromJS(json: MasterTerritoryModelType): MasterTerritory {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.outletsInfo = TerritoryOutletInfo.fromJS(state.outletsInfo);
        state.metrics = TerritoryMetrics.fromJS(state.metrics);
        return new this(Immutable.Map(state));
    }

    toJS(): MasterTerritoryModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            code: this.code,
            status: this.status,
            version: this.version,
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            outerPolygon: this.outerPolygon,
            outletsInfo: this.outletsInfo.toJS(),
            metrics: this.metrics.toJS()
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get outerPolygon(): OuterPolygonType {
        return this._state.get('outerPolygon');
    }

    setOuterPolygon(outerPolygon: OuterPolygonType): this {
        return this.clone(this._state.set('outerPolygon', outerPolygon));
    }

    get outletsInfo(): TerritoryOutletInfo {
        return this._state.get('outletsInfo');
    }

    setOutletsInfo(outletsInfo: TerritoryOutletInfo): this {
        return this.clone(this._state.set('outletsInfo', outletsInfo));
    }

    get metrics(): TerritoryMetrics {
        return this._state.get('metrics');
    }

    setMetrics(metrics: TerritoryMetrics): this {
        return this.clone(this._state.set('metrics', metrics));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterTerritoryPlan extends ImmutableModel {
    static fromJS(json: MasterTerritoryPlanModelType): MasterTerritoryPlan {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.territories = Immutable.List(state.territories).map(item =>
            MasterTerritory.fromJS(item)
        );
        state.actor = Actor.fromJS(state.actor);
        return new this(Immutable.Map(state));
    }

    toJS(): MasterTerritoryPlanModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            code: this.code,
            status: this.status,
            version: this.version,
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            territories: this.territories.toArray().map(item => item.toJS()),
            actor: this.actor.toJS()
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get territories(): Immutable.List<MasterTerritory> {
        return this._state.get('territories');
    }

    setTerritories(territories: Immutable.List<MasterTerritory>): this {
        return this.clone(this._state.set('territories', territories));
    }

    get actor(): Actor {
        return this._state.get('actor');
    }

    setActor(actor: Actor): this {
        return this.clone(this._state.set('actor', actor));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TerritoryMetrics extends ImmutableModel {
    static fromJS(json: TerritoryMetricsModelType): TerritoryMetrics {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): TerritoryMetricsModelType {
        return {
            count: this.count,
            totalRevenue: this.totalRevenue,
            totalTAT: this.totalTAT
        };
    }

    get count(): number {
        return this._state.get('count');
    }

    setCount(count: number): this {
        return this.clone(this._state.set('count', count));
    }

    get totalRevenue(): number {
        return this._state.get('totalRevenue');
    }

    setTotalRevenue(totalRevenue: number): this {
        return this.clone(this._state.set('totalRevenue', totalRevenue));
    }

    get totalTAT(): string {
        return this._state.get('totalTAT');
    }

    setTotalTAT(totalTAT: string): this {
        return this.clone(this._state.set('totalTAT', totalTAT));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TerritoryOutletId extends ImmutableModel {
    static fromJS(json: TerritoryOutletIdModelType): TerritoryOutletId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): TerritoryOutletIdModelType {
        const js: TerritoryOutletIdModelType = {
            clientId: this.clientId,
            outletId: this.outletId
        };

        if (this.version != null) {
            js.version = this.version;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get outletId(): string {
        return this._state.get('outletId');
    }

    setOutletId(outletId: string): this {
        return this.clone(this._state.set('outletId', outletId));
    }

    get version(): ?number {
        return this._state.get('version');
    }

    setVersion(version: ?number): this {
        return this.clone(this._state.set('version', version));
    }
}
